import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/layout/Layout';

const SignInPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Real-time validation states
  const [emailValid, setEmailValid] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  
  const { signIn } = useAuth();
  const navigate = useNavigate();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  // Handle email input - clear errors immediately when typing again
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailTouched(true);
    
    // If there was an error and user starts typing again, clear it immediately
    if (!emailValid && value.length > 0) {
      setEmailValid(true);
      if (error === 'Please enter a valid email address (e.g., user@example.com)') {
        setError('');
      }
    }
  };

  // Handle password input - clear errors immediately when typing again
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordTouched(true);
    
    // If there was an error and user starts typing again, clear it immediately
    if (!passwordValid && value.length > 0) {
      setPasswordValid(true);
      if (error === 'Password must be at least 8 characters long') {
        setError('');
      }
    }
  };

  // Handle validation when user exits email field
  const handleEmailBlur = () => {
    if (email.length > 0) {
      const isValid = validateEmail(email);
      setEmailValid(isValid);
      if (!isValid) {
        setError('Please enter a valid email address (e.g., user@example.com)');
      } else if (error === 'Please enter a valid email address (e.g., user@example.com)') {
        setError('');
      }
    } else {
      setEmailValid(true);
      if (error === 'Please enter a valid email address (e.g., user@example.com)') {
        setError('');
      }
    }
  };

  // Handle validation when user exits password field
  const handlePasswordBlur = () => {
    if (password.length > 0) {
      const isValid = validatePassword(password);
      setPasswordValid(isValid);
      if (!isValid) {
        setError('Password must be at least 8 characters long');
      } else if (error === 'Password must be at least 8 characters long') {
        setError('');
      }
    } else {
      setPasswordValid(true);
      if (error === 'Password must be at least 8 characters long') {
        setError('');
      }
    }
  };

  // Debounced validation for email (0.75 seconds after user stops typing)
  useEffect(() => {
    if (!emailTouched) return;

    const timeoutId = setTimeout(() => {
      if (email.length > 0) {
        const isValid = validateEmail(email);
        setEmailValid(isValid);
        if (!isValid) {
          setError('Please enter a valid email address (e.g., user@example.com)');
        } else if (error === 'Please enter a valid email address (e.g., user@example.com)') {
          setError('');
        }
      } else {
        setEmailValid(true);
        if (error === 'Please enter a valid email address (e.g., user@example.com)') {
          setError('');
        }
      }
    }, 750); // 0.75 seconds

    return () => clearTimeout(timeoutId);
  }, [email, emailTouched, error]);

  // Debounced validation for password (0.75 seconds after user stops typing)
  useEffect(() => {
    if (!passwordTouched) return;

    const timeoutId = setTimeout(() => {
      if (password.length > 0) {
        const isValid = validatePassword(password);
        setPasswordValid(isValid);
        if (!isValid) {
          setError('Password must be at least 8 characters long');
        } else if (error === 'Password must be at least 8 characters long') {
          setError('');
        }
      } else {
        // Don't set passwordValid = true for empty fields
        // Just clear password-related errors
        if (error === 'Password must be at least 8 characters long') {
          setError('');
        }
      }
    }, 750);

    return () => clearTimeout(timeoutId);
  }, [password, passwordTouched, error]);

  // Update the button logic to be more straightforward:
  const isFormValid = email.length > 0 && password.length >= 8 && emailValid && passwordValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Mark all fields as touched for validation display
    setEmailTouched(true);
    setPasswordTouched(true);

    // Validate all fields before submitting
    const emailIsValid = validateEmail(email);
    const passwordIsValid = validatePassword(password);
    
    setEmailValid(emailIsValid);
    setPasswordValid(passwordIsValid);

    if (!emailIsValid) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }

    if (!passwordIsValid) {
      setError('Password must be at least 8 characters long.');
      setIsLoading(false);
      return;
    }

    try {
      const result = await signIn(email, password);
      
             if (result.success) {
         navigate('/portal');
       } else {
        setError(result.error || 'Sign in failed. Please try again.');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      setError('Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="ph-page">
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="max-w-lg w-full mx-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              {/* Header with Logo */}
              <div className="text-center mb-8">
                <div className="mb-6">
                  <img 
                    src="/ProductHub-Logo.bmp" 
                    alt="ProductHub Logo" 
                    className="mx-auto h-20 object-contain"
                  />
                </div>
                <h1 className="text-3xl font-bold mb-4" style={{color: 'var(--ph-main-blue)'}}>
                  ProductHub Alliance Portal
                </h1>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Access our comprehensive directory of supply chain and industry professionals, companies, and GS1 offices.<br /><br />
                  This secure portal provides authenticated access to our alliance partner network, registration forms, and partnership resources across all sectors.
                </p>
              </div>

              {/* Back to Home Link */}
              <div className="text-center mb-6">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
                >
                  ← Back to Home
                </button>
              </div>

          {/* Sign In Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                  emailTouched && !emailValid
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-producthub-primary'
                }`}
                placeholder="Enter your email address"
              />
              {/* Email error handled in main error area below */}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  required
                  className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    passwordTouched && !passwordValid
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-producthub-primary'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password error handled in main error area below */}
            </div>

            <div className={`rounded-lg p-3 transition-all duration-200 mb-4 ${
              error 
                ? 'bg-red-50 border border-red-200 opacity-100' 
                : 'bg-transparent border border-transparent opacity-0'
            }`} style={{ minHeight: '2.75rem' }}>
              <p className={`text-sm transition-opacity duration-200 ${
                error ? 'text-red-700 opacity-100' : 'text-transparent opacity-0'
              }`}>
                {error || 'No error message'}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="w-1/2 bg-producthub-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-producthub-primary focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mx-auto block"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

              {/* Contact Info */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-gray-600 text-sm mb-1">
                  Need access or have questions?
                </p>
                <p className="text-sm">
                  <span style={{color: 'var(--ph-dark-grey)'}}>Contact us at </span>
                  <a
                    href="mailto:support@producthub.co?subject=Enquiry from ProductHub Alliance Portal"
                    className="hover:underline font-medium"
                    style={{color: 'var(--ph-main-blue)'}}
                  >
                    support@producthub.co
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SignInPage; 